/**
 * useUrgencyPricing — Deadline-based pricing with countdown timer
 *
 * Tiers:
 *   "womensday" — Mar 8, 2026 until midnight UB time (UTC+8) — extra 10% off sale price
 *   "sale"      — before Mar 31, 2026 23:59:59 (discounted price)
 *   "normal"    — after deadline (full price)
 *
 * Pricing:
 *   USA/Canada/Australia travel: ₮850,000 (НӨАТ орсон)
 *   Schengen travel: sale ₮650,000 / normal ₮850,000 (НӨАТ орсон)
 *   USA study (F-1): $1,300 ($300 урьдчилгаа + $1,000 виз гарсны дараа)
 *   Ireland language school: sale ₮990,000 / normal ₮1,190,000
 *   Czech work permit: €2,200 (fixed, no discount)
 *   Australia study: $1,300 (fixed, no discount)
 */
import { useEffect, useState } from "react";

// Women's Day promo: Mar 8, 2026 midnight Ulaanbaatar time (UTC+8)
// UTC equivalent: Mar 8 2026 16:00:00 UTC (midnight UB = 16:00 UTC)
const WOMENS_DAY_DEADLINE = new Date("2026-03-08T16:00:00Z").getTime();

// Deadline: Mar 31, 2026 23:59:59 Ulaanbaatar time (UTC+8)
// UTC equivalent: Mar 31 2026 15:59:59 UTC
const SALE_DEADLINE = new Date("2026-03-31T15:59:59Z").getTime();

/**
 * Check if Women's Day promo is currently active
 */
export function isWomensDayPromoActive(now: number = Date.now()): boolean {
  return now < WOMENS_DAY_DEADLINE;
}

export function getWomensDayDeadline(): number {
  return WOMENS_DAY_DEADLINE;
}

// Australia promo removed — no longer active
export const AUSTRALIA_PROMO_PRICE = "850,000₮";
export const AUSTRALIA_NORMAL_SALE_PRICE = "850,000₮";

export function isAustraliaPromoActive(): boolean {
  return false;
}

export function getAustraliaPromoDeadline(): number {
  return SALE_DEADLINE;
}

export type PricingTier = "womensday" | "sale" | "normal";

export interface VisaPricing {
  visaKey: string;
  visaName: string;
  salePrice: string;       // discounted service fee (discounted price)
  normalPrice: string;     // full service fee
  childPrice?: string;
  currency: "MNT" | "USD" | "EUR";
  // For USD visas: deposit + remainder breakdown
  saleDeposit?: string;
  saleRemainder?: string;
  normalDeposit?: string;
  normalRemainder?: string;
  // Czech: 3-part payment
  czechInstallments?: string;
  // Whether 10% family discount applies
  familyDiscountEligible: boolean;
  // Visa government fee (paid separately to embassy)
  visaFeeNote?: string;    // short description of the visa fee
  visaFeeAmount?: string;  // amount per person
  visaFeePaidWhen?: string; // when it's paid
}

export const VISA_PRICING: VisaPricing[] = [
  // ── Travel visas (MNT) ──────────────────────────────────────
  {
    visaKey: "usa-travel",
    visaName: "АНУ аяллын 10 жилийн виз (B1/B2)",
    salePrice: "850,000₮",
    normalPrice: "850,000₮",
    childPrice: "100,000₮",
    currency: "MNT",
    familyDiscountEligible: true,
    visaFeeNote: "Элчин сайдын яамны визний хураамж (MRV fee)",
    visaFeeAmount: "$185 / хүн",
    visaFeePaidWhen: "Ярилцлагын өдрөөс өмнө онлайнаар",
  },
  {
    visaKey: "canada-travel",
    visaName: "Канадын аяллын виз (TRV)",
    salePrice: "850,000₮",
    normalPrice: "850,000₮",
    childPrice: "100,000₮",
    currency: "MNT",
    familyDiscountEligible: true,
    visaFeeNote: "Засгийн газрын визний хураамж + биометр",
    visaFeeAmount: "CAD $100 + $85 / хүн",
    visaFeePaidWhen: "Виз мэдүүлэхдээ онлайнаар",
  },
  {
    visaKey: "australia-travel",
    visaName: "Австралийн аяллын виз (Subclass 600)",
    salePrice: "850,000₮",
    normalPrice: "850,000₮",
    childPrice: "100,000₮",
    currency: "MNT",
    familyDiscountEligible: true,
    visaFeeNote: "Австралийн засгийн газрын визний хураамж",
    visaFeeAmount: "AUD $190 / хүн",
    visaFeePaidWhen: "Виз мэдүүлэхдээ онлайнаар",
  },
  {
    visaKey: "schengen-travel",
    visaName: "Шенгенийн улсуудын аяллын виз",
    salePrice: "850,000₮",
    normalPrice: "850,000₮",
    childPrice: "100,000₮",
    currency: "MNT",
    familyDiscountEligible: true,
    visaFeeNote: "Шенгений визний хураамж",
    visaFeeAmount: "€90 / хүн (12-с доош хүүхэд €45)",
    visaFeePaidWhen: "Виз мэдүүлэхдээ элчин сайдын яаманд",
  },
  // ── USA Study visa (USD) ─────────────────────────────────────
  {
    visaKey: "usa-study",
    visaName: "АНУ сургалтын виз (F-1)",
    salePrice: "$1,300",
    normalPrice: "$1,500",
    childPrice: "Үнэгүй",
    currency: "USD",
    saleDeposit: "$300",
    saleRemainder: "$1,000 (виз гарсны дараа)",
    normalDeposit: "$600",
    normalRemainder: "$900 (виз гарсны дараа)",
    familyDiscountEligible: false,
    visaFeeNote: "Визний хураамж (MRV) + SEVIS хураамж",
    visaFeeAmount: "$185 + $350",
    visaFeePaidWhen: "Ярилцлагаас өмнө онлайнаар",
  },
  // ── Australia Study visa (USD) ───────────────────────────────
  {
    visaKey: "australia-study",
    visaName: "Австралийн сургалтын виз (Subclass 500)",
    salePrice: "$1,300",
    normalPrice: "$1,300",
    childPrice: "Үнэгүй",
    currency: "USD",
    saleDeposit: "$400",
    saleRemainder: "$900 (виз гарсны дараа)",
    normalDeposit: "$400",
    normalRemainder: "$900 (виз гарсны дараа)",
    familyDiscountEligible: false,
    visaFeeNote: "Австралийн засгийн газрын визний хураамж",
    visaFeeAmount: "AUD $710",
    visaFeePaidWhen: "Виз мэдүүлэхдээ онлайнаар",
  },
  // ── Ireland Language School visa (MNT) ──────────────────────
  {
    visaKey: "ireland-study",
    visaName: "Ирландын хэлний сургуулийн виз",
    salePrice: "990,000₮",
    normalPrice: "1,190,000₮",
    currency: "MNT",
    familyDiscountEligible: true,
    visaFeeNote: "Ирландын визний хураамж",
    visaFeeAmount: "€100 / хүн",
    visaFeePaidWhen: "Виз мэдүүлэхдээ элчин сайдын яаманд",
  },
  // ── Chicago Tour Package (USD) ────────────────────────────────
  {
    visaKey: "chicago-tour",
    visaName: "Чикаго хөтөлбөрт аялал (6 шөнө 7 өдөр)",
    salePrice: "$3,990",
    normalPrice: "$3,990",
    currency: "USD",
    saleDeposit: "$500",
    saleRemainder: "$3,490 (аялалаас 30 хоногийн өмнө)",
    normalDeposit: "$500",
    normalRemainder: "$3,490 (аялалаас 30 хоногийн өмнө)",
    familyDiscountEligible: false,
    visaFeeNote: "АНУ-ын визтэй бол $185 хасагдана",
    visaFeeAmount: "$185 / хүн (визгүй бол)",
    visaFeePaidWhen: "Ярилцлагаас өмнө онлайнаар",
  },
  // ── Czech Work permit (EUR) ──────────────────────────────────
  {
    visaKey: "czech-work",
    visaName: "Чех Employee Card — ажлын зөвшөөрөл",
    salePrice: "€2,465",
    normalPrice: "€2,465",
    currency: "EUR",
    czechInstallments: "Гэрээ: €1,700 + визийн өмнө: €765",
    familyDiscountEligible: false,
    visaFeeNote: "€765 төлбөрт даатгал, визийн улсын хураамж багтсан",
    visaFeeAmount: "Багтсан",
    visaFeePaidWhen: "Визэнд орохоос өмнө",
  },
  // ── Self-apply bundles (Өөрөө мэдүүлэх багцууд) ────────────────────────────
  {
    visaKey: "usa-self-bundle",
    visaName: "АНУ Өөрөө мэдүүлэх багц (Бүх зааварчилгаа)",
    salePrice: "129,000₮",
    normalPrice: "129,000₮",
    currency: "MNT",
    familyDiscountEligible: false,
  },
  {
    visaKey: "australia-self-bundle",
    visaName: "Австрали Өөрөө мэдүүлэх багц (Бүх зааварчилгаа)",
    salePrice: "129,000₮",
    normalPrice: "129,000₮",
    currency: "MNT",
    familyDiscountEligible: false,
  },
  {
    visaKey: "canada-self-bundle",
    visaName: "Канад Өөрөө мэдүүлэх багц (Бүх зааварчилгаа)",
    salePrice: "129,000₮",
    normalPrice: "129,000₮",
    currency: "MNT",
    familyDiscountEligible: false,
  },
  {
    visaKey: "ireland-self-bundle",
    visaName: "Ирланд Өөрөө мэдүүлэх багц (Бүх зааварчилгаа)",
    salePrice: "129,000₮",
    normalPrice: "129,000₮",
    currency: "MNT",
    familyDiscountEligible: false,
  },
  {
    visaKey: "schengen-self-bundle",
    visaName: "Шенген Өөрөө мэдүүлэх багц (Бүх зааварчилгаа)",
    salePrice: "129,000₮",
    normalPrice: "129,000₮",
    currency: "MNT",
    familyDiscountEligible: false,
  },
  {
    visaKey: "newzealand-self-bundle",
    visaName: "Шинэ Зеланд Өөрөө мэдүүлэх багц (Бүх зааварчилгаа)",
    salePrice: "129,000₮",
    normalPrice: "129,000₮",
    currency: "MNT",
    familyDiscountEligible: false,
  },
];

/**
 * Get current pricing tier based on deadline
 */
export function calcTier(now: number = Date.now()): PricingTier {
  if (now < WOMENS_DAY_DEADLINE) return "womensday";
  if (now < SALE_DEADLINE) return "sale";
  return "normal";
}

/**
 * Get time remaining until deadline
 */
export function getTimeLeft(now: number = Date.now()): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
} {
  const remaining = SALE_DEADLINE - now;
  if (remaining <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds, expired: false };
}

/**
 * Get the current price for a visa based on tier
 * womensday = 10% off the sale price
 */
export function getCurrentPrice(visa: VisaPricing, tier: PricingTier): string {
  if (tier === "normal") return visa.normalPrice;
  const base = visa.salePrice;
  if (tier === "sale") return base;
  // womensday: 10% off sale price (only for MNT visas; USD/EUR fixed)
  if (visa.currency !== "MNT") return base;
  const num = parseFloat(base.replace(/[^0-9.]/g, "").replace(/,/g, ""));
  if (isNaN(num)) return base;
  const discounted = Math.round(num * 0.9);
  return discounted.toLocaleString() + "₮";
}

/**
 * Get family member price (10% discount off the visa price)
 * Returns null if no family discount applies
 */
export function getFamilyPrice(visa: VisaPricing, tier: PricingTier): string | null {
  if (!visa.familyDiscountEligible) return null;
  const base = getCurrentPrice(visa, tier);
  const num = parseFloat(base.replace(/[^0-9.]/g, "").replace(/,/g, ""));
  if (isNaN(num)) return null;
  const discounted = Math.round(num * 0.9);
  if (visa.currency === "MNT") return discounted.toLocaleString() + "₮";
  if (visa.currency === "USD") return "$" + discounted.toLocaleString("en-US");
  if (visa.currency === "EUR") return "€" + discounted.toLocaleString("en-US");
  return null;
}

/**
 * useUrgencyPricing hook
 */
export function useUrgencyPricing(): {
  tier: PricingTier;
  timeLeft: { days: number; hours: number; minutes: number; seconds: number; expired: boolean };
  deadlineDate: string;
} {
  const [tier, setTier] = useState<PricingTier>(() => calcTier());
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft());

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTier(calcTier(now));
      setTimeLeft(getTimeLeft(now));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return {
    tier,
    timeLeft,
    deadlineDate: tier === "womensday" ? "Өнөөдөр шөнийн 12:00 хүртэл" : "2026 оны 3 сарын 31",
  };
}
